import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft, Leaf } from "@phosphor-icons/react";
import { ReopenCookieLink } from "@/components/CookieConsent";

/**
 * Impressum nach § 5 ECG (AT), § 14 UGB, § 25 Mediengesetz und § 63 GewO.
 */
export default function Impressum() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="impressum-root">
      <Helmet>
        <title>Impressum · Bodenblick</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <LegalHeader />

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
          data-testid="impressum-back"
        >
          <CaretLeft size={14} /> Zurück zur Startseite
        </Link>

        <div>
          <div className="eid-label">Rechtliches</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Impressum</h1>
          <p className="text-sm text-zinc-400 mt-2">
            Informationspflichten laut § 5 E-Commerce-Gesetz (ECG), § 14
            Unternehmensgesetzbuch (UGB) und § 25 Mediengesetz (MedienG).
          </p>
        </div>

        <Section title="Medieninhaberin, Herausgeberin &amp; Diensteanbieterin">
          <p>
            <strong>Johanna Maislinger</strong>
            <br />
            Einzelunternehmerin
            <br />
            Steinbruch 5<br />
            4822 Bad Ischl
            <br />
            Österreich
          </p>
        </Section>

        <Section title="Kontakt">
          <p>
            E-Mail:{" "}
            <a href="mailto:vision@bodenblick.com" className="underline">
              vision@bodenblick.com
            </a>
            <br />
            Web: <a href="https://bodenblick.com" className="underline">bodenblick.com</a>
          </p>
        </Section>

        <Section title="Unternehmensgegenstand">
          <p>
            Entwicklung und Betrieb der Web-Anwendung <em>Bodenblick</em> — ein
            wöchentlicher Datendienst zu Satelliten-, Wetter- und Agrarmarktdaten
            für landwirtschaftliche und forstwirtschaftliche Betriebe.
          </p>
        </Section>

        <Section title="Anwendbare Rechtsvorschriften">
          <p>
            Gewerbeordnung (GewO):{" "}
            <a
              className="underline"
              href="https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10007517"
              target="_blank"
              rel="noreferrer"
            >
              www.ris.bka.gv.at
            </a>
          </p>
          <p>Aufsichtsbehörde: Bezirkshauptmannschaft Gmunden.</p>
        </Section>

        <Section title="Inhaltlich verantwortlich gemäß § 25 MedienG">
          <p>Johanna Maislinger, Anschrift wie oben.</p>
        </Section>

        <Section title="Blattlinie">
          <p>
            Unabhängige Berichterstattung und Daten-Analyse zu ökologischen und
            agrarwirtschaftlichen Themen auf Basis öffentlich zugänglicher
            Satelliten- und Wetterdaten. Keine Anlageberatung, keine Rechtsberatung.
          </p>
        </Section>

        <Section title="Online-Streitbeilegung">
          <p>
            Verbraucher:innen haben die Möglichkeit, Beschwerden an die Online-
            Streitbeilegungs-Plattform der EU zu richten:{" "}
            <a
              className="underline"
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noreferrer"
            >
              ec.europa.eu/consumers/odr
            </a>
            . Wir sind weder bereit noch verpflichtet, an Streitbeilegungsverfahren
            vor einer Verbraucher­schlichtungs­stelle teilzunehmen.
          </p>
        </Section>

        <Section title="Haftung für Inhalte">
          <p>
            Die Inhalte werden mit größtmöglicher Sorgfalt erstellt. Für Richtigkeit,
            Vollständigkeit und Aktualität der Daten — insbesondere für aus
            Satelliten- und Wettermodellen abgeleitete Kennzahlen und daraus
            generierte Handlungs­empfehlungen — kann jedoch keine Haftung übernommen
            werden. Die Nutzung erfolgt auf eigene Verantwortung und ersetzt keine
            fachliche Vor-Ort-Beurteilung.
          </p>
        </Section>

        <Section title="Urheberrecht">
          <p>
            Texte, Grafiken, Code und Gestaltung dieser Website sind urheberrechtlich
            geschützt. Eine Verwendung außerhalb des persönlichen Gebrauchs bedarf
            der vorherigen schriftlichen Zustimmung. Satellitendaten stammen vom
            Copernicus-Programm der EU (© ESA/EU) und Open-Meteo, Marktdaten von
            öffentlichen Finanzquellen.
          </p>
        </Section>

        <div className="pt-4 border-t border-white/10 text-xs text-zinc-500 font-mono">
          Stand: {new Date().toLocaleDateString("de-AT", { year: "numeric", month: "long" })}
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}

export function LegalHeader() {
  return (
    <header className="border-b border-white/10 bg-[#121212]">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
          <span className="p-1.5 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
            <Leaf size={18} />
          </span>
          <div>
            <div className="eid-label">Bodenblick</div>
            <div className="text-sm font-medium">bodenblick.com</div>
          </div>
        </Link>
        <Link
          to="/login"
          className="text-xs text-zinc-400 hover:text-white"
          data-testid="legal-login-link"
        >
          Anmelden →
        </Link>
      </div>
    </header>
  );
}

export function LegalFooter() {
  return (
    <footer className="border-t border-white/10 bg-[#121212] mt-8">
      <div className="max-w-5xl mx-auto px-6 py-6 flex flex-wrap gap-4 justify-between text-xs text-zinc-500 font-mono">
        <span>Bodenblick · © {new Date().getFullYear()} Johanna Maislinger</span>
        <div className="flex gap-3 flex-wrap">
          <Link to="/impressum" className="hover:text-white" data-testid="footer-impressum">
            Impressum
          </Link>
          <Link to="/datenschutz" className="hover:text-white" data-testid="footer-datenschutz">
            Datenschutz
          </Link>
          <Link to="/agb" className="hover:text-white" data-testid="footer-agb">
            Nutzungsbedingungen
          </Link>
          <ReopenCookieLink />
          <a href="mailto:vision@bodenblick.com" className="hover:text-white">
            vision@bodenblick.com
          </a>
        </div>
      </div>
    </footer>
  );
}

export function Section({ title, children }) {
  return (
    <section className="eid-card p-5 space-y-2">
      <h2 className="text-lg font-medium tracking-tight">{title}</h2>
      <div className="text-sm text-white leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
